import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import axios from "axios";
import apiUrls from "../../../../api";
import { formatContentTypeName } from "../../../graph/util/NameFormatter";
import { exportInteractionToCsv } from "../../utils/exportCsv";
import InteractionContentRenderer from "./InteractionContentRenderer";
import type { SessionSubdata } from "../../types/sessionTypes";

// Extended type for API response with session info
interface AttemptData extends SessionSubdata {
  successRate: number;
  session?: {
    id: number;
    end_user?: {
      id: number;
      username?: string;
      email?: string;
      source_avatar?: {
        s3_url?: string;
      };
    };
    createdAt?: string;
  };
}

interface InteractionAttemptsModalProps {
  contentId: number;
  contentName: string;
  contentType: string;
  projectId: number;
  startDate?: string;
  endDate?: string;
  onClose: () => void;
}

type ViewState = "list" | "detail";

const InteractionAttemptsModal = ({
  contentId,
  contentName,
  contentType,
  projectId,
  startDate,
  endDate,
  onClose,
}: InteractionAttemptsModalProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "fr" ? fr : enUS;

  const [attempts, setAttempts] = useState<AttemptData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState<ViewState>("list");
  const [selectedAttempt, setSelectedAttempt] = useState<AttemptData | null>(null);
  const [sortKey, setSortKey] = useState<"date" | "successRate">("date");
  const [sortAsc, setSortAsc] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    const fetchAttempts = async () => {
      setIsLoading(true);
      try {
        let url = `${apiUrls.stats.interactionAttempts}?contentId=${contentId}&projectId=${projectId}`;
        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;

        const response = await axios.get(url);
        // Transform data to include rawContentType for InteractionContentRenderer
        const transformedData = (response.data || []).map((item: AttemptData) => ({
          ...item,
          rawContentType: item.content?.type || contentType,
        }));
        setAttempts(transformedData);
      } catch (error) {
        console.error("Error fetching interaction attempts:", error);
        setAttempts([]);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchAttempts();
  }, [contentId, contentType, projectId, startDate, endDate]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getScoreColor = (score: number, maxScore: number): string => {
    if (maxScore === 0) return "text-gray-600";
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const handleSort = (key: "date" | "successRate") => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === "successRate");
    }
  };

  // Normalize accents for search comparison
  const normalizeAccents = (str: string): string => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  // Filter attempts by search query (matches username, email, date, or "anonymous")
  const filteredAttempts = useMemo(() => {
    if (!searchQuery.trim()) return attempts;
    const query = normalizeAccents(searchQuery.toLowerCase().trim());

    // Check if searching for anonymous users
    const anonymousTerms = ["anonyme", "anonymous"];
    const isSearchingAnonymous = anonymousTerms.some((term) => term.includes(query));

    return attempts.filter((attempt) => {
      const endUser = attempt.session?.end_user;
      const username = normalizeAccents(endUser?.username?.toLowerCase() || "");
      const email = normalizeAccents(endUser?.email?.toLowerCase() || "");

      // Check if user is anonymous (no end_user or no username/email)
      const isAnonymous = !endUser || (!endUser.username && !endUser.email);

      // Match anonymous users if searching for "anonyme" or "anonymous"
      if (isSearchingAnonymous && isAnonymous) {
        return true;
      }

      // Match username or email
      if (username.includes(query) || email.includes(query)) {
        return true;
      }

      // Match date (formatted as displayed + full month name)
      if (attempt.session?.createdAt) {
        const date = new Date(attempt.session.createdAt);
        // Short format (as displayed in table)
        const dateStrShort = normalizeAccents(
          format(date, "dd MMM yyyy HH:mm", { locale }).toLowerCase(),
        );
        // Full month name for better search
        const dateStrFull = normalizeAccents(
          format(date, "dd MMMM yyyy HH:mm", { locale }).toLowerCase(),
        );
        if (dateStrShort.includes(query) || dateStrFull.includes(query)) {
          return true;
        }
      }

      return false;
    });
  }, [attempts, searchQuery, locale]);

  const sortedAttempts = [...filteredAttempts].sort((a, b) => {
    const multiplier = sortAsc ? 1 : -1;
    if (sortKey === "date") {
      const dateA = a.session?.createdAt ? new Date(a.session.createdAt).getTime() : 0;
      const dateB = b.session?.createdAt ? new Date(b.session.createdAt).getTime() : 0;
      return (dateA - dateB) * multiplier;
    }
    return (a.successRate - b.successRate) * multiplier;
  });

  // Summary stats using successRate
  const avgSuccessRate =
    attempts.length > 0
      ? Math.round(attempts.reduce((sum, a) => sum + a.successRate, 0) / attempts.length)
      : 0;

  const handleAttemptClick = (attempt: AttemptData) => {
    setSelectedAttempt(attempt);
    setCurrentView("detail");
  };

  const handleBackToList = () => {
    setCurrentView("list");
    setTimeout(() => setSelectedAttempt(null), 300);
  };

  const handleExportInteraction = async () => {
    if (!selectedAttempt) return;
    const localeCode = i18n.language === "fr" ? "fr" : "en";
    await exportInteractionToCsv(selectedAttempt, {
      filename: "interaction",
      locale: localeCode,
    });
  };

  return (
    <div className="stats-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="stats-modal-content w-full max-w-6xl h-[80vh] max-h-[800px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Carousel container */}
        <div
          className="flex transition-transform duration-300 ease-in-out h-full"
          style={{
            transform: currentView === "detail" ? "translateX(-50%)" : "translateX(0)",
            width: "200%",
          }}
        >
          {/* Panel 1: Attempts list */}
          <div className="w-1/2 flex-shrink-0 flex flex-col h-full">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white p-6 border-b border-gray-200 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{contentName}</h2>
                  <p className="text-sm text-gray-500 mt-1">{formatContentTypeName(contentType)}</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Summary stats */}
              {!isLoading && attempts.length > 0 && (
                <div className="flex items-stretch gap-4 mt-6">
                  {/* Attempts */}
                  <div className="group relative flex flex-col items-center px-6 py-3 border-r border-gray-200 cursor-help">
                    <span className="text-xs text-gray-500 mb-1">
                      {t("statsNew.interactions.attempts")}
                    </span>
                    <span className="text-xl font-bold text-gray-900">{attempts.length}</span>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
                      {t("statsNew.interactions.attemptsTooltip")}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                    </div>
                  </div>
                  {/* Avg Score */}
                  <div className="group relative flex flex-col items-center px-6 py-3 cursor-help">
                    <span className="text-xs text-gray-500 mb-1">
                      {t("statsNew.interactions.avgScore")}
                    </span>
                    <span
                      className={`text-xl font-bold ${
                        avgSuccessRate >= 50 ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {avgSuccessRate}%
                    </span>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
                      {t("statsNew.interactions.avgScoreTooltip")}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                    </div>
                  </div>
                </div>
              )}

              {/* Search bar */}
              {!isLoading && attempts.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="relative max-w-xs">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t("general.search")}
                      className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : attempts.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {t("statsNew.interactions.noAttempts")}
                </div>
              ) : sortedAttempts.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {t("statsNew.sessionDetail.noInteractions")}
                </div>
              ) : (
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort("date")}
                      >
                        <div className="flex items-center gap-1">
                          {t("statsNew.interactions.date")}
                          {sortKey === "date" && <span>{sortAsc ? "↑" : "↓"}</span>}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("statsNew.interactions.user")}
                      </th>
                      <th
                        className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort("successRate")}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {t("statsNew.interactions.score")}
                          {sortKey === "successRate" && <span>{sortAsc ? "↑" : "↓"}</span>}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        {t("statsNew.interactions.duration")}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t("statsNew.sessionDetail.moreDetails")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedAttempts.map((attempt) => (
                      <tr
                        key={attempt.id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleAttemptClick(attempt)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {attempt.session?.createdAt
                            ? format(new Date(attempt.session.createdAt), "dd MMM yyyy HH:mm", {
                                locale,
                              })
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {attempt.session?.end_user?.username ||
                            attempt.session?.end_user?.email ||
                            t("statsNew.interactions.anonymous")}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`font-semibold ${getScoreColor(
                              attempt.score,
                              attempt.scoreMax || 1,
                            )}`}
                          >
                            {Math.round(attempt.score)}/{attempt.scoreMax || 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-center">
                          {formatDuration(attempt.time_end_content - attempt.time_start_content)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 text-green-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                              />
                            </svg>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Panel 2: Attempt detail */}
          <div className="w-1/2 flex-shrink-0 flex flex-col h-full">
            {/* Header with back button */}
            <div className="sticky top-0 z-10 bg-white p-4 border-b border-gray-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleBackToList}
                    className="p-2 text-green-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                  </button>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedAttempt?.content?.name || contentName}
                    <span className="text-gray-400 font-normal">
                      {" "}
                      - {formatContentTypeName(contentType)}
                    </span>
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Stats cards */}
            {selectedAttempt && (
              <div className="sticky top-[57px] z-10 bg-white p-4 border-b border-gray-200 shadow-sm">
                <div className="flex items-stretch gap-3">
                  {/* Score */}
                  <div className="flex flex-col items-center justify-center px-5 py-3 border border-gray-200 rounded-lg min-w-[80px]">
                    <span className="text-xs text-gray-500 mb-1">
                      {t("statsNew.interaction.score")}
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      {Math.round(selectedAttempt.score)}/{selectedAttempt.scoreMax || 1}
                    </span>
                  </div>

                  {/* Duration */}
                  <div className="flex flex-col items-center justify-center px-5 py-3 border border-gray-200 rounded-lg min-w-[80px]">
                    <span className="text-xs text-gray-500 mb-1">
                      {t("statsNew.interaction.duration")}
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      {formatDuration(
                        selectedAttempt.time_end_content - selectedAttempt.time_start_content,
                      )}
                    </span>
                  </div>

                  {/* User */}
                  <div className="flex flex-col items-center justify-center px-5 py-3 border border-gray-200 rounded-lg min-w-[80px]">
                    <span className="text-xs text-gray-500 mb-1">User</span>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                        {selectedAttempt.session?.end_user?.source_avatar?.s3_url ? (
                          <img
                            src={selectedAttempt.session.end_user.source_avatar.s3_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 text-gray-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                        {selectedAttempt.session?.end_user?.username ||
                          selectedAttempt.session?.end_user?.email ||
                          t("statsNew.interactions.anonymous")}
                      </span>
                    </div>
                  </div>

                  {/* Spacer */}
                  <div className="flex-1" />

                  {/* Export button */}
                  <button
                    onClick={handleExportInteraction}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors self-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    <span className="text-sm font-medium">Export CSV</span>
                  </button>
                </div>
              </div>
            )}

            {/* Interaction content */}
            <div className="flex-1 p-6 overflow-hidden">
              {selectedAttempt && <InteractionContentRenderer interaction={selectedAttempt} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractionAttemptsModal;
